import {
    List, Datagrid, TextField, BooleanField, DateField,
    Edit, Create, TextInput, BooleanInput, SelectInput,
    ReferenceArrayInput, SelectArrayInput, ReferenceArrayField,
    TabbedForm, FormTab, useRecordContext, useTranslate, useListContext,
    TopToolbar, ListButton,
} from 'react-admin';
import { Box, Typography, Card, CardContent, Grid, Chip } from '@mui/material';
import { APPLICATION_CHOICES, ApplicationField } from './roles';

const productFilters = [
    <SelectInput key="application" source="application" choices={APPLICATION_CHOICES} alwaysOn />,
];

export const ProductList = () => (
    <List filters={productFilters}>
        <Datagrid rowClick="edit">
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="description" />
            <ApplicationField />
            <BooleanField source="active" />
            <DateField source="createdAt" showTime />
        </Datagrid>
    </List>
);

const ProductTitle = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    return <span>{record ? `${translate('resources.products.name', { smart_count: 1 })}: ${record.name}` : ''}</span>;
};

const MetadataCard = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translate('resources.products.tabs.metadata')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {translate('resources.products.fields.createdAt')}
                        </Typography>
                        <Typography variant="body2">
                            {record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {translate('resources.products.fields.updatedAt')}
                        </Typography>
                        <Typography variant="body2">
                            {record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '-'}
                        </Typography>
                    </Grid>
                    {record.uuid && (
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                UUID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                {record.uuid}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

const PermissionChips = () => {
    const { data, isLoading } = useListContext();

    if (isLoading || !data) return null;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {data.map((permission: any) => (
                <Chip
                    key={permission.id}
                    label={permission.name}
                    size="small"
                    variant="outlined"
                />
            ))}
        </Box>
    );
};

const PermissionsPreview = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record?.permissions || record.permissions.length === 0) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {translate('resources.products.fields.currentPermissions')} ({record.permissions.length})
            </Typography>
            <ReferenceArrayField source="permissions" reference="permissions">
                <PermissionChips />
            </ReferenceArrayField>
        </Box>
    );
};

export const ProductEdit = () => {
    const translate = useTranslate();

    return (
        <Edit title={<ProductTitle />} actions={<TopToolbar><ListButton /></TopToolbar>}>
            <TabbedForm>
                <FormTab label={translate('resources.products.tabs.general')}>
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <TextInput
                            source="name"
                            fullWidth
                            required
                        />
                        <TextInput
                            source="description"
                            fullWidth
                            multiline
                            rows={4}
                        />
                        <SelectInput
                            source="application"
                            choices={APPLICATION_CHOICES}
                            defaultValue="academy"
                            fullWidth
                        />
                        <BooleanInput source="active" />
                        <MetadataCard />
                    </Box>
                </FormTab>
                <FormTab label={translate('resources.products.tabs.permissions')}>
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <PermissionsPreview />
                        <Box sx={{ mt: 2 }}>
                            <ReferenceArrayInput source="permissions" reference="permissions" sort={{ field: 'name', order: 'ASC' }}>
                                <SelectArrayInput
                                    optionText="name"
                                    fullWidth
                                />
                            </ReferenceArrayInput>
                        </Box>
                    </Box>
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};

export const ProductCreate = () => {
    const translate = useTranslate();

    return (
        <Create actions={<TopToolbar><ListButton /></TopToolbar>}>
            <TabbedForm>
                <FormTab label={translate('resources.products.tabs.general')}>
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <TextInput
                            source="name"
                            fullWidth
                            required
                        />
                        <TextInput
                            source="description"
                            fullWidth
                            multiline
                            rows={4}
                        />
                        <SelectInput
                            source="application"
                            choices={APPLICATION_CHOICES}
                            defaultValue="academy"
                            fullWidth
                        />
                        <BooleanInput source="active" defaultValue={true} />
                    </Box>
                </FormTab>
                <FormTab label={translate('resources.products.tabs.permissions')}>
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <ReferenceArrayInput source="permissions" reference="permissions" sort={{ field: 'name', order: 'ASC' }}>
                            <SelectArrayInput
                                optionText="name"
                                fullWidth
                            />
                        </ReferenceArrayInput>
                    </Box>
                </FormTab>
            </TabbedForm>
        </Create>
    );
};
